import * as React from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import { parse, format, isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("fr", fr);

/**
 * DatePicker — wraps react-datepicker with shadcn/ui Input styling.
 *
 * Props:
 *   value      — string in yyyy-MM-dd format (or empty)
 *   onChange   — called with yyyy-MM-dd string (or "" on clear)
 *   placeholder
 *   disabled
 *   minDate    — string yyyy-MM-dd
 *   maxDate    — string yyyy-MM-dd
 *   className
 */
const DatePicker = ({
  value,
  onChange,
  placeholder = "jj/mm/aaaa",
  disabled = false,
  minDate,
  maxDate,
  className,
  ...rest
}) => {
  const { t } = useTranslation();

  const parseDate = (str) => {
    if (!str) return null;
    // Tolerate ISO datetime values (e.g. "2026-06-05T00:00:00") coming from
    // DateTime backend columns — keep only the yyyy-MM-dd part.
    const dateOnly = String(str).split("T")[0];
    const d = parse(dateOnly, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : null;
  };

  const selected = parseDate(value);
  const minDateObj = parseDate(minDate);
  const maxDateObj = parseDate(maxDate);

  const handleChange = (date) => {
    if (!date) {
      onChange("");
      return;
    }
    onChange(format(date, "yyyy-MM-dd"));
  };

  return (
    <ReactDatePicker
      selected={selected}
      onChange={handleChange}
      disabled={disabled}
      locale="fr"
      dateFormat="dd/MM/yyyy"
      placeholderText={placeholder}
      minDate={minDateObj}
      maxDate={maxDateObj}
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      isClearable={!disabled}
      autoComplete="off"
      previousMonthButtonLabel={t("datePicker.previousMonth")}
      nextMonthButtonLabel={t("datePicker.nextMonth")}
      previousYearButtonLabel={t("datePicker.previousYear")}
      nextYearButtonLabel={t("datePicker.nextYear")}
      customInput={
        <input
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...rest}
        />
      }
    />
  );
};

export { DatePicker };
